import { createSelector } from 'reselect';
import type { LegacyProductReference, LineNumber } from 'constants/products';
import { getStateAtNamespaceKey } from 'redux/get-state-at-namespace-key';
import { formatAsPositivePoundsOrPence } from 'utils/currency';
import { getAllCookies, getIsFunctionalCookiesConsented } from 'utils/cookie';
import { getProductConflicts } from 'redux/modules/conflicts/selectors';
import { getSearchObject } from 'redux/modules/routing/selectors';
import { COOKIE_QUICK_SHOP_VIEW } from 'constants/cookies';
import type { RecommendedSelection } from 'redux/transforms/types';
import { STATUS_INELIGIBLE } from 'constants/quick-shop';
import { getQuickShopResolvedView } from '../get-quick-shop-resolved-view';
import type { QuickShopState, QuickShopSuccessState } from '../types';
import { QUICK_SHOP_PAGE_SIZE, TRISTATE_TRUE } from '../constants';

const isEligibilitySuccessfullyLoaded = (state: QuickShopState): state is QuickShopSuccessState =>
  'eligibility' in state;

const isSuggestionsSuccessfullyLoaded = (state: QuickShopState): state is QuickShopSuccessState =>
  'suggestions' in state;

const getQuickShop = (state: WtrState) => getStateAtNamespaceKey(state, 'quickShop');

const getEligibility = createSelector(getQuickShop, quickShop =>
  isEligibilitySuccessfullyLoaded(quickShop) ? quickShop.eligibility : null,
);

export const getIneligibleReason = createSelector(getEligibility, eligibility =>
  eligibility?.status === STATUS_INELIGIBLE ? eligibility.reason : null,
);

const EMPTY_SUGGESTIONS_ARRAY: never[] = [];

export const getHasSuggestionsLoaded = createSelector([getQuickShop], quickShop =>
  isSuggestionsSuccessfullyLoaded(quickShop),
);

export const getSuggestions = createSelector([getQuickShop], quickShop =>
  isSuggestionsSuccessfullyLoaded(quickShop) ? quickShop.suggestions : EMPTY_SUGGESTIONS_ARRAY,
);

export const getSuggestionsLoading = createSelector(
  getQuickShop,
  quickShop => quickShop.loading ?? true,
);

export const getTopSuggestionsCount = createSelector(getQuickShop, quickShop =>
  isSuggestionsSuccessfullyLoaded(quickShop)
    ? Math.min(quickShop.totalResults, QUICK_SHOP_PAGE_SIZE)
    : 0,
);

export const getTopSuggestions = createSelector(
  [getSuggestions, getTopSuggestionsCount],
  (suggestions, topSuggestionsCount) => suggestions.slice(0, topSuggestionsCount),
);

export const getTopSuggestionsLength = createSelector(
  [getTopSuggestions],
  suggestions => suggestions.length,
);

export const getHasTopSuggestions = createSelector([getTopSuggestionsLength], length => length > 0);

export const getTopSuggestionsWithoutConflicts = createSelector(
  [getTopSuggestions, getProductConflicts as (state: WtrState) => unknown],
  (suggestions: LegacyProductReference[], conflicts) =>
    suggestions.filter(suggestion => !conflicts[suggestion.searchProduct]),
);

export const getTopSuggestionsWithoutConflictsLength = createSelector(
  [getTopSuggestionsWithoutConflicts],
  suggestions => suggestions.length,
);

export const getAllSuggestionsInTrolley = createSelector(getQuickShop, (quickShop): boolean =>
  isSuggestionsSuccessfullyLoaded(quickShop)
    ? quickShop.allSuggestionsInTrolley === TRISTATE_TRUE
    : false,
);

export const getAnySuggestionsInTrolley = createSelector(getQuickShop, (quickShop): boolean =>
  isSuggestionsSuccessfullyLoaded(quickShop)
    ? quickShop.anySuggestionsInTrolley === TRISTATE_TRUE
    : false,
);

export const getAllShoppableTopSuggestionsInTrolley = createSelector(
  [getAllSuggestionsInTrolley, getAnySuggestionsInTrolley, getTopSuggestionsWithoutConflictsLength],
  (allSuggestionsInTrolley, anySuggestionsInTrolley, topSuggestionsWithoutConflictsLength) =>
    allSuggestionsInTrolley ||
    (anySuggestionsInTrolley && topSuggestionsWithoutConflictsLength === 0),
);

export const getEstimatedPriceLoading = createSelector(
  getQuickShop,
  quickShop => quickShop.estimatedPriceLoading ?? true,
);

export const getEstimatedPrice = createSelector(
  [getQuickShop, getEstimatedPriceLoading],
  (quickShop, loading) => {
    if (typeof quickShop.estimatedPrice !== 'undefined') {
      return formatAsPositivePoundsOrPence(quickShop.estimatedPrice);
    }

    if (loading) {
      return '';
    }

    return 'to be calculated at trolley';
  },
);

const EMPTY_RECOMMENDED_SELECTION: RecommendedSelection[] = [];

export const getRecommendedSelection = createSelector(getQuickShop, quickShop =>
  isSuggestionsSuccessfullyLoaded(quickShop)
    ? quickShop.recommendedSelection
    : EMPTY_RECOMMENDED_SELECTION,
);

const EMPTY_AVAILABLE_LINE_NUMBERS: Set<LineNumber> = new Set();

export const getAvailableLineNumbers = createSelector(getSuggestions, suggestions => {
  if (suggestions.length === 0) {
    return EMPTY_AVAILABLE_LINE_NUMBERS;
  }

  return new Set<LineNumber>(suggestions.map(({ searchProduct }) => searchProduct));
});

export const getQuickShopView = (state: WtrState) => {
  const search = getSearchObject(state);
  const cookies = getAllCookies();
  const cookiesConsented = getIsFunctionalCookiesConsented(cookies);
  const savedCookieQuickShopViewPreference = cookies[COOKIE_QUICK_SHOP_VIEW];

  return getQuickShopResolvedView({
    cookiesConsented,
    savedCookieQuickShopViewPreference,
    urlView: search.view,
  });
};
